import React from 'react';
import { withTranslation } from 'react-i18next';
import AppLogo from './AppLogo';

/**
 * H3 — the failure mode code splitting introduces, and only that one.
 *
 * Before the split there was one bundle: a tab left open across a deploy kept
 * working, because everything it would ever need was already in memory. Now a
 * page is fetched on demand by its hashed filename, and a deploy retires that
 * filename. So the tab that has been open since this morning tries to open
 * Settings, asks for a chunk that no longer exists, and — without this — renders
 * nothing at all. A blank screen, from a user's point of view, for pressing a
 * nav button. A reload fixes it completely, because reloading fetches the new
 * `index.html` with the new filenames in it.
 *
 * WHY THIS TELLS THE TWO CASES APART. The first version of this file said "a
 * new version of Fiwara is available" for *every* error thrown anywhere in the
 * subtree. That is a lie in the common case and an actively harmful one: a
 * genuine crash inside a page was reported as a routine update, the reload
 * button did nothing, and the real error was visible only in the console. It
 * turned every bug on a lazily-loaded page into the same unfalsifiable message.
 *
 * So: a chunk that failed to load gets the reload, because reloading is the
 * fix. Anything else is a bug, and says so — with the message, so whoever hits
 * it can report something more useful than "it says there is a new version".
 *
 * A FAILED CHUNK IS NOT PROOF OF A DEPLOY EITHER. The browser reports a dropped
 * connection, a blocked request or a flaky mobile network exactly the way it
 * reports a chunk a deploy retired — so the second version still said "a new
 * version is available" whenever anything went wrong with the download, which
 * was usually untrue. Now the boundary asks the server: it fetches a fresh
 * `index.html` and compares the main bundle it names with the one this tab is
 * running. Only a different name is a new version; the same name means the
 * download itself failed, and no answer at all means the server is unreachable.
 */

// The main bundle this tab booted from, e.g. `/static/js/main.1a2b3c4d.js`.
const MAIN_BUNDLE = /\/static\/js\/main\.[0-9a-f]+\.js/;

const runningBundle = () => {
    const script = Array.from(document.scripts).find((el) => MAIN_BUNDLE.test(el.getAttribute('src') || ''));
    return script ? script.getAttribute('src').match(MAIN_BUNDLE)[0] : null;
};

// 'updated' | 'sameBuild' | 'offline'. Never throws.
export const checkForNewBuild = async () => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
    let html;
    try {
        // no-store: the point is the server's current answer, not the cache's.
        const response = await fetch('/index.html', { cache: 'no-store' });
        if (!response.ok) return 'offline';
        html = await response.text();
    } catch {
        return 'offline';
    }
    const served = (html.match(MAIN_BUNDLE) || [])[0];
    const running = runningBundle();
    // Without both names there is nothing to compare, and claiming an update
    // on a guess is the exact lie this check exists to stop.
    return served && running && served !== running ? 'updated' : 'sameBuild';
};

// What a failed dynamic import looks like across bundlers and browsers. Matched
// loosely on purpose: a false negative here costs the reload screen on a chunk
// that genuinely failed, which is recoverable; a false positive hides a bug.
const isChunkLoadError = (error) => {
    if (!error) return false;
    const name = String(error.name || '');
    const message = String(error.message || '');
    return (
        name === 'ChunkLoadError'
        || /Loading chunk \S+ failed/i.test(message)
        || /Loading CSS chunk/i.test(message)
        || /Failed to fetch dynamically imported module/i.test(message)
        || /error loading dynamically imported module/i.test(message)
        || /Importing a module script failed/i.test(message)
    );
};

// A class component because that is still the only way to catch a render error.
class ChunkErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, build: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Always worth the console line — especially for the stale-chunk case,
        // which is otherwise invisible once the reload fixes it.
        console.error('Error inside a lazy boundary:', error, info);
        if (isChunkLoadError(error)) {
            checkForNewBuild().then((build) => this.setState({ build }));
        }
    }

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;

        const { t } = this.props;
        const reloadButton = (label) => (
            <button className="btn btn-grad" onClick={() => window.location.reload()}>
                {label}
            </button>
        );

        if (!isChunkLoadError(error)) {
            return (
                <div className="boot open">
                    <AppLogo />
                    <p>{t('general.unexpectedError')}</p>
                    {/* The real message, so there is something to report. */}
                    {error.message && (
                        <p className="hint" style={{ maxWidth: '40ch', textAlign: 'center' }}>{error.message}</p>
                    )}
                    {reloadButton(t('general.reload'))}
                </div>
            );
        }

        const { build } = this.state;
        // Still asking the server: say nothing rather than guess.
        if (!build) {
            return (
                <div className="boot open">
                    <AppLogo />
                    <p>{t('general.loading')}</p>
                </div>
            );
        }

        // The raw chunk message is not shown on any of these: it is noise over
        // an instruction that already works.
        if (build === 'updated') {
            return (
                <div className="boot open">
                    <AppLogo />
                    <p>{t('general.updateAvailable')}</p>
                    {reloadButton(t('general.reload'))}
                </div>
            );
        }

        return (
            <div className="boot open">
                <AppLogo />
                <p>{t(build === 'offline' ? 'general.offline' : 'general.loadFailed')}</p>
                <p className="hint" style={{ maxWidth: '40ch', textAlign: 'center' }}>
                    {t(build === 'offline' ? 'general.offlineHint' : 'general.loadFailedHint')}
                </p>
                {reloadButton(t('general.tryAgain'))}
            </div>
        );
    }
}

export default withTranslation()(ChunkErrorBoundary);
